import { useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Loading } from '../../components/layout/Loading';
import { useTickets, useTriageTicket, useDecideTicket } from '../../hooks';
import { useAuthStore } from '../../lib/store';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/toast';
import { CATEGORY_LABELS, STATUS_LABELS, STATUS_COLOR } from '../Tickets';
import { resolveFileUrl } from '../../lib/api-client';

/** Content-only version used by the Approvals hub (tabs). */
export function HRTicketsContent() {
  const { data: tickets, isLoading } = useTickets();
  const triage = useTriageTicket();
  const decide = useDecideTicket();
  const role = useAuthStore((s) => s.profile?.role ?? 'HR');
  const canDecide = role === 'MANAGER' || role === 'OWNER';
  const toast = useToast();
  const [noteModal, setNoteModal] = useState<{ id: string; kind: 'triage' | 'decide'; sub: string; required: boolean; title: string } | null>(null);
  const [note, setNote] = useState('');

  function doTriage(id: string, action: 'review' | 'escalate' | 'resolve') {
    const titles = { review: 'รับเรื่อง — บันทึก HR (ไม่บังคับ)', escalate: 'ส่งผู้บริหาร — ระบุเหตุผล', resolve: 'ปิดเรื่อง — บันทึก HR (ไม่บังคับ)' };
    setNoteModal({ id, kind: 'triage', sub: action, required: action === 'escalate', title: titles[action] });
    setNote('');
  }
  function doDecide(id: string, decision: 'approved' | 'rejected') {
    setNoteModal({ id, kind: 'decide', sub: decision, required: true, title: `เหตุผลการ${decision === 'approved' ? 'อนุมัติ' : 'ไม่อนุมัติ'}` });
    setNote('');
  }
  async function submitNote() {
    if (!noteModal) return;
    if (noteModal.required && !note.trim()) { toast.error('กรุณาระบุเหตุผล'); return; }
    try {
      if (noteModal.kind === 'triage') {
        await triage.mutateAsync({ id: noteModal.id, action: noteModal.sub as 'review' | 'escalate' | 'resolve', hr_note: note.trim() || undefined });
      } else {
        await decide.mutateAsync({ id: noteModal.id, decision: noteModal.sub as 'approved' | 'rejected', decision_note: note.trim() });
      }
      setNoteModal(null);
      toast.success('บันทึกแล้ว');
    } catch (e) { toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'); }
  }

  return (
    <>
      {isLoading ? (
        <Loading />
      ) : !tickets || tickets.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-8">ยังไม่มี ticket</p>
      ) : (
        <div className="space-y-2">
          {tickets.map((t) => {
            const active = t.status === 'open' || t.status === 'under_review';
            return (
              <div key={t.id} className="card p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-xs text-gray-500">{CATEGORY_LABELS[t.category]} · {t.emp_code}</div>
                    <div className="font-medium">{t.subject}</div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${STATUS_COLOR[t.status]}`}>
                    {STATUS_LABELS[t.status]}
                  </span>
                </div>
                <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{t.detail}</p>
                {t.ref_note && <p className="text-xs text-gray-500 mt-1">อ้างอิง: {t.ref_note}</p>}
                {t.attachments?.length > 0 && (
                  <div className="mt-2">
                    <div className="text-xs text-gray-500 mb-1">หลักฐานแนบ ({t.attachments.length}):</div>
                    <div className="flex flex-wrap gap-2">
                      {t.attachments.map((a, i) => (
                        <a key={i} href={resolveFileUrl(a.url) ?? '#'} target="_blank" rel="noopener noreferrer"
                          className="text-xs text-blue-600 underline bg-blue-50 rounded px-2 py-1 truncate max-w-[180px]">📎 {a.name}</a>
                      ))}
                    </div>
                  </div>
                )}
                {t.hr_note && <p className="text-xs text-blue-700 mt-1">HR: {t.hr_note}</p>}
                {t.decision_note && <p className="text-xs text-gray-700 mt-1">ผล ({t.decided_role}): {t.decision_note}</p>}

                <div className="flex flex-wrap gap-2 mt-3">
                  {active && (
                    <>
                      <button onClick={() => doTriage(t.id, 'review')} className="btn-secondary text-xs">รับเรื่อง</button>
                      <button onClick={() => doTriage(t.id, 'escalate')} className="btn-secondary text-xs">ส่งผู้บริหาร</button>
                      <button onClick={() => doTriage(t.id, 'resolve')} className="btn-secondary text-xs">ปิดเรื่อง</button>
                    </>
                  )}
                  {t.status === 'escalated' && canDecide && (
                    <>
                      <button onClick={() => doDecide(t.id, 'approved')} className="btn-primary text-xs">อนุมัติ</button>
                      <button onClick={() => doDecide(t.id, 'rejected')} className="btn-danger text-xs">ไม่อนุมัติ</button>
                    </>
                  )}
                  {t.status === 'escalated' && !canDecide && (
                    <span className="text-xs text-gray-400 self-center">รอผู้บริหาร (Manager/Owner) อนุมัติ</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal open={noteModal !== null} onClose={() => setNoteModal(null)} title={noteModal?.title}>
        <div className="space-y-3">
          <textarea className="input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder={noteModal?.required ? 'ระบุเหตุผล…' : 'พิมพ์บันทึก (ไม่บังคับ)…'} />
          <div className="flex gap-2">
            <button className="btn-secondary flex-1" onClick={() => setNoteModal(null)}>ยกเลิก</button>
            <button className="btn-primary flex-1" onClick={submitNote} disabled={triage.isPending || decide.isPending}>ยืนยัน</button>
          </div>
        </div>
      </Modal>
    </>
  );
}

/** Standalone page (kept for direct linking from legacy URLs). */
export function HRTickets() {
  return (
    <AdminLayout title="เรื่องร้องเรียน / คำขอ (Tickets)">
      <HRTicketsContent />
    </AdminLayout>
  );
}
